import React from 'react';
import { useLocation } from 'react-router-dom';
import { captureEvent } from '@/posthog-analytics/posthog';
import { useAuth } from '../useAuth';
import { NameCaptureForm } from './NameCaptureForm';
import { useUpdateMyName } from './useUpdateMyName';
import {
  allowsInterstitial,
  isPromptDismissed,
  markPromptDismissed,
  nameSuggestion,
  needsName,
} from './profileCompletion';

/** Asks for a name once per session, for anyone the login step could not catch.
 *
 *  Signing in with Google or Microsoft is a full-page redirect, so those people
 *  come back already authenticated with no modal on screen and never reach
 *  `LoginModal`'s third step. This covers them, and covers anyone who was
 *  already signed in when the feature shipped.
 *
 *  Mounted in `SuspenseLayout` rather than `AuthContext` or `ProtectedRoute`:
 *  `AuthContext` sits above `RouterProvider`, so it has no `useLocation` and
 *  could not keep this away from a live interview, and `ProtectedRoute` does not
 *  wrap the public job pages a signed-in candidate can be on.
 */
export const ProfileNameGate: React.FC = () => {
  const { user, isAuthenticated } = useAuth();
  const { pathname } = useLocation();
  const [dismissed, setDismissed] = React.useState(isPromptDismissed);

  const suggestion = nameSuggestion(user);
  const updateName = useUpdateMyName({ surface: 'interstitial', prefilled: suggestion });

  const shouldAsk =
    isAuthenticated && needsName(user) && allowsInterstitial(pathname) && !dismissed;
  if (!shouldAsk) return null;

  const skip = () => {
    captureEvent('profile_name_prompt_skipped', { surface: 'interstitial' });
    markPromptDismissed();
    setDismissed(true);
  };

  return (
    <div className="fixed inset-0 flex items-center justify-center bg-black/60 backdrop-blur-sm z-50">
      <div className="bg-white p-8 rounded-xl shadow-2xl max-w-md w-full relative animate-fadeIn">
        <NameCaptureForm
          initialFirstName={suggestion.first}
          initialLastName={suggestion.last}
          suggestionSource={suggestion.source}
          variant="interstitial"
          isSaving={updateName.isPending}
          error={updateName.isError ? 'Could not save your name. Please try again.' : null}
          allowSkip
          onSubmit={(firstName, lastName) =>
            updateName.mutate({ body: { first_name: firstName, last_name: lastName } })
          }
          onSkip={skip}
        />
      </div>
    </div>
  );
};

export default ProfileNameGate;
